/* ============================================================
   VariantPicker — Modal showing all variants for a section type
   Displayed when user clicks "Change Layout" in PageStructure.
   ============================================================ */

import { getSectionVariants, getVariantDef, type VariantDef } from '@/lib/cms/component-registry'
import { getSectionDef } from '@/lib/cms/section-fields'

interface VariantPickerProps {
  sectionKey: string
  currentVariant: string
  onSelect: (variantKey: string) => void
  onClose: () => void
}

export default function VariantPicker({ sectionKey, currentVariant, onSelect, onClose }: VariantPickerProps) {
  const variants = getSectionVariants(sectionKey)
  const sectionDef = getSectionDef(sectionKey)
  const currentDef = getVariantDef(sectionKey, currentVariant)

  if (variants.length === 0) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} role="presentation">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-[#0f0f1a] border border-white/[0.08] rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Choose {sectionDef?.label || sectionKey} Layout
            </h3>
            <p className="text-sm text-white/30 mt-0.5">
              Currently using: <span className="text-white/50">{currentDef?.label || currentVariant}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/40 hover:text-white/80 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Variant grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {variants.map((variant: VariantDef) => {
            const isActive = variant.key === currentVariant
            return (
              <button
                key={variant.key}
                type="button"
                onClick={() => { onSelect(variant.key); onClose() }}
                className={`text-left rounded-xl overflow-hidden border transition-[background-color,border-color] duration-200 group ${
                  isActive
                    ? 'border-blue-500/50 ring-1 ring-blue-500/30 bg-blue-500/5'
                    : 'border-white/[0.06] hover:border-white/[0.15] bg-white/[0.02] hover:bg-white/[0.04]'
                }`}
              >
                {/* Thumbnail */}
                <div
                  className="h-28 w-full flex items-center justify-center text-3xl relative"
                  style={{ background: variant.thumbnail }}
                >
                  <span className="opacity-40 group-hover:opacity-70 transition-opacity">{variant.icon}</span>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-white">{variant.label}</p>
                  <p className="text-xs text-white/30 mt-0.5 line-clamp-1">{variant.description}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
